import React, { useState, useEffect, useRef } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { theme } from './theme';
import { TabType } from './types';
import type { LifecycleState } from './services/api';
import ClientsTab from './components/ClientsTab';
import PipelineView from './components/PipelineView';
import SettingsTab from './components/SettingsTab';
import TermsTab from './components/TermsTab';
import RecommendationsCatalogTab from './components/RecommendationsCatalogTab';
import { DashboardView } from './components/DashboardView';
import NewVersionBanner from './components/NewVersionBanner';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuth } from './contexts/AuthContext';
import { Icon, IconName } from './ds/components/Icon';

import { API_BASE_URL } from './services/api';

// --- Nav model -------------------------------------------------------------
// Top-level entries use DS Icon glyphs (home/building/users/settings)
// matching the AdminShell nav. „Budynki" is the single source of truth for the
// inspection domain. Atrapy „Ogłoszenia"/„Faktury" usunięte (właściciel 2026-07-29).

type NavLeaf = { kind: 'leaf'; tab: TabType; label: string; icon: IconName };

const NAV_TOP: NavLeaf = { kind: 'leaf', tab: 'dashboard', label: 'Pulpit', icon: 'home' };

// „Budynki" — skalowalny widok pipeline (spec §3): chipy stanów + szukajka +
// lista server-side. Wejście do planowania/CEEB per budynek. JEDNO źródło prawdy
// o budynkach. Grupa „Przeglądy" (4 skróty z prefiltrem lifecycle, #9) USUNIĘTA
// (właściciel 2026-07-09) — powielała domenę „Budynki"; prefiltr etapu żyje już
// tylko jako drill-down z Pulpitu (DashboardView.onNavigateToStage → buildingsStage).
const NAV_BUILDINGS: NavLeaf = { kind: 'leaf', tab: 'buildings', label: 'Budynki', icon: 'building' };

// H1 (2026-08-04): kalendarz terminow wszystkich budynkow - odpowiada na pytanie
// "co mnie czeka w tym tygodniu", ktorego widok Budynki (per budynek) nie obsluguje.
const NAV_TERMS: NavLeaf = { kind: 'leaf', tab: 'terms', label: 'Terminy', icon: 'calendar' };

const NAV_REST: NavLeaf[] = [
  // Nomenklatura: „Zarządca", nie „Klient" (decyzja właściciela 2026-08-04) —
  // identyfikatory (tab 'clients', encja clients) celowo bez zmian.
  { kind: 'leaf', tab: 'clients', label: 'Zarządcy', icon: 'users' },
  { kind: 'leaf', tab: 'settings', label: 'Ustawienia', icon: 'settings' },
];

// Zakładki widoczne WYŁĄCZNIE dla superadmina (właściciel, 2026-07-28). Katalog zaleceń
// steruje treścią, która trafia do oficjalnego protokołu CEEB — nie może go otworzyć ani
// edytować zwykły admin. Backend pilnuje tego niezależnie (requireSuperadmin na każdym zapisie
// katalogu A/B/C/D, pozycji zaleceń lokali i list urządzeń — od 2026-10-10, D9).
const NAV_SUPERADMIN: NavLeaf[] = [
  { kind: 'leaf', tab: 'recommendations', label: 'Zalecenia', icon: 'clipboard' },
];

const TAB_TITLES: Record<TabType, string> = {
  'dashboard': 'Pulpit',
  'buildings': 'Budynki',
  'terms': 'Terminy',
  'clients': 'Zarządcy',
  'recommendations': 'Zalecenia',
  'settings': 'Ustawienia',
};

// --- DS-styled sidebar nav button (mirrors AdminShell.Sidebar button) ------
function NavButton({
  active,
  icon,
  label,
  onClick,
  inset = false,
  trailing,
}: {
  active: boolean;
  icon: IconName;
  label: string;
  onClick: () => void;
  inset?: boolean;
  trailing?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        width: '100%',
        minHeight: 'var(--touch-min)',
        padding: inset ? '0 var(--space-4) 0 var(--space-8)' : '0 var(--space-4)',
        textAlign: 'left',
        fontFamily: 'var(--font-sans)',
        fontSize: inset ? 'var(--text-label)' : 'var(--text-body)',
        fontWeight: active ? 'var(--weight-bold)' : 'var(--weight-medium)',
        color: active ? 'var(--blue-700)' : 'var(--text-primary)',
        background: active ? 'var(--changed-bg)' : 'transparent',
        border: 'none',
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
      }}
    >
      <Icon
        name={icon}
        size={inset ? 22 : 26}
        color={active ? 'var(--action-primary)' : 'var(--text-secondary)'}
      />
      <span style={{ flex: 1 }}>{label}</span>
      {trailing}
    </button>
  );
}

function App() {
  const { user, logout, token } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  // Prefiltr etapu dla „Budynki" — ustawiany przez drill-down z Pulpitu (#10),
  // zerowany przy zwykłym wejściu z sidebara (żeby stary prefiltr nie zaskakiwał).
  const [buildingsStage, setBuildingsStage] = useState<LifecycleState | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Load company logo
  useEffect(() => {
    const loadCompanyLogo = async () => {
      if (!token || !user?.companyId) return;

      try {
        const response = await fetch(`${API_BASE_URL}/companies/${user.companyId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.logoUrl) {
            setCompanyLogo(data.logoUrl);
          }
        }
      } catch (error) {
        console.error('Error loading company logo:', error);
      }
    };

    loadCompanyLogo();
  }, [token, user?.companyId]);

  // Close profile dropdown on outside click. W7: pointerdown (nie mousedown) — jednolicie
  // dla myszy i dotyku (iPad Safari nie zawsze emituje mousedown pod palcem).
  useEffect(() => {
    if (!menuOpen) return;
    const onDocPointerDown = (e: PointerEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => document.removeEventListener('pointerdown', onDocPointerDown);
  }, [menuOpen]);

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
  };

  const getRoleName = (role: string) => {
    switch (role) {
      case 'superadmin':
        return 'Superadmin';
      case 'admin':
        return 'Administrator';
      case 'sweep':
        return 'Kominiarz';
      default:
        return role;
    }
  };

  // Role → DS status tint (red = superadmin/elevated, blue = admin action role,
  // green = OK/field role). Kept as a token mapping (was MUI Chip color prop).
  const getRoleTint = (
    role: string
  ): { bg: string; fg: string } => {
    switch (role) {
      case 'superadmin':
        return { bg: 'var(--status-deny-bg)', fg: 'var(--status-deny)' };
      case 'admin':
        return { bg: 'var(--changed-bg)', fg: 'var(--changed-text)' };
      case 'sweep':
        return { bg: 'var(--status-ok-bg)', fg: 'var(--status-ok)' };
      default:
        return { bg: 'var(--surface-fill)', fg: 'var(--text-secondary)' };
    }
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <DashboardView
            onNavigate={setActiveTab}
            onNavigateToStage={(stage) => {
              setBuildingsStage(stage);
              setActiveTab('buildings');
            }}
          />
        );
      case 'buildings':
        // key wymusza remount przy zmianie prefiltru (initialStage czytany raz, na mount).
        return <PipelineView key={buildingsStage ?? 'active'} initialStage={buildingsStage} />;
      case 'terms':
        return <TermsTab />;
      case 'clients':
        return <ClientsTab />;
      case 'recommendations':
        // Druga bramka (poza ukryciem w nawigacji): zwykły admin nie zobaczy katalogu nawet
        // przy odziedziczonym `activeTab` (np. po zmianie konta bez przeładowania strony).
        return isSuperadmin
          ? <RecommendationsCatalogTab />
          : <div style={{ padding: 'var(--space-6)', color: 'var(--text-secondary)' }}>
              Katalog zaleceń jest dostępny wyłącznie dla superadministratora.
            </div>;
      case 'settings':
        return <SettingsTab />;
      default:
        return null;
    }
  };

  const roleTint = user ? getRoleTint(user.role) : null;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NewVersionBanner />
      <ProtectedRoute>
        <div
          style={{
            display: 'flex',
            height: '100vh',
            width: '100%',
            background: 'var(--surface-page)',
            fontFamily: 'var(--font-sans)',
            color: 'var(--text-primary)',
          }}
        >
          {/* ---- Sidebar (DS / AdminShell look) ---- */}
          <nav
            style={{
              width: 240,
              flex: 'none',
              height: '100%',
              boxSizing: 'border-box',
              background: 'var(--surface-panel)',
              borderRight: 'var(--border-width) solid var(--border)',
              display: 'flex',
              flexDirection: 'column',
              padding: 'var(--space-5) var(--space-4)',
              overflowY: 'auto',
            }}
          >
            {/* Brand / company logo header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: '0 var(--space-3) var(--space-6)',
              }}
            >
              {companyLogo ? (
                <img
                  src={companyLogo}
                  alt="Logo firmy"
                  style={{
                    height: 40,
                    width: 'auto',
                    maxWidth: 160,
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 40,
                      height: 40,
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--surface-fill)',
                    }}
                  >
                    <Icon name="flame" size={24} color="var(--text-secondary)" />
                  </span>
                  <span
                    style={{
                      fontSize: 'var(--text-body)',
                      fontWeight: 'var(--weight-bold)',
                      color: 'var(--text-heading)',
                    }}
                  >
                    Przeglądy
                  </span>
                </>
              )}
            </div>

            {/* Nav items */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {/* Pulpit */}
              <NavButton
                active={activeTab === NAV_TOP.tab}
                icon={NAV_TOP.icon}
                label={NAV_TOP.label}
                onClick={() => setActiveTab(NAV_TOP.tab)}
              />

              {/* Budynki — pipeline view (chipy + szukajka + lista server-side).
                  Jedyne wejście do domeny przeglądów; prefiltr etapu (buildingsStage)
                  ustawia drill-down z Pulpitu, ale sam wpis pozostaje aktywny. */}
              <NavButton
                active={activeTab === NAV_BUILDINGS.tab}
                icon={NAV_BUILDINGS.icon}
                label={NAV_BUILDINGS.label}
                onClick={() => {
                  setBuildingsStage(null);
                  setActiveTab(NAV_BUILDINGS.tab);
                }}
              />

              {/* Terminy — kalendarz terminow wszystkich budynkow (H1). */}
              <NavButton
                active={activeTab === NAV_TERMS.tab}
                icon={NAV_TERMS.icon}
                label={NAV_TERMS.label}
                onClick={() => setActiveTab(NAV_TERMS.tab)}
              />

              {/* Klienci / Ustawienia (+ Zalecenia dla superadmina) */}
              {[...NAV_REST, ...(isSuperadmin ? NAV_SUPERADMIN : [])].map((n) => (
                <NavButton
                  key={n.tab}
                  active={activeTab === n.tab}
                  icon={n.icon}
                  label={n.label}
                  onClick={() => setActiveTab(n.tab)}
                />
              ))}
            </div>
          </nav>

          {/* ---- Main column ---- */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {/* Top bar (DS / AdminShell look) */}
            <header
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: 'var(--topbar-height)',
                padding: '0 var(--space-7)',
                flex: 'none',
                background: 'var(--surface-panel)',
                borderBottom: 'var(--border-width) solid var(--border)',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--text-h2)',
                  fontWeight: 'var(--weight-bold)',
                  color: 'var(--text-heading)',
                }}
              >
                {TAB_TITLES[activeTab]}
              </span>

              {user && roleTint && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                  {/* Role chip — icon + color + text (DS status convention) */}
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                      padding: '6px var(--space-3)',
                      borderRadius: 'var(--radius-pill)',
                      background: roleTint.bg,
                      color: roleTint.fg,
                      fontSize: 'var(--text-caption)',
                      fontWeight: 'var(--weight-semibold)',
                    }}
                  >
                    {getRoleName(user.role)}
                  </span>

                  {/* Profile dropdown — imię + ikona jako JEDEN przycisk toggle (W7:
                      wcześniej imię było nieklikalnym spanem, menu otwierała tylko ikona). */}
                  <div ref={menuRef} style={{ position: 'relative' }}>
                    <button
                      type="button"
                      onClick={() => setMenuOpen((o) => !o)}
                      aria-haspopup="menu"
                      aria-expanded={menuOpen}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        minHeight: 44,
                        padding: '0 var(--space-2) 0 var(--space-3)',
                        borderRadius: 'var(--radius-pill)',
                        background: 'var(--surface-fill)',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--text-secondary)',
                        touchAction: 'manipulation',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 'var(--text-label)',
                          fontWeight: 'var(--weight-medium)',
                          color: 'var(--text-secondary)',
                        }}
                      >
                        {user.fullName}
                      </span>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32,
                          height: 32,
                          borderRadius: 'var(--radius-pill)',
                          background: 'var(--surface-card)',
                        }}
                      >
                        <Icon name="users" size={22} />
                      </span>
                    </button>

                    {menuOpen && (
                      <div
                        role="menu"
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + var(--space-2))',
                          right: 0,
                          minWidth: 220,
                          maxHeight: 'calc(100vh - var(--topbar-height) - var(--space-4))',
                          overflowY: 'auto',
                          background: 'var(--surface-panel)',
                          border: 'var(--border-width) solid var(--border)',
                          borderRadius: 'var(--radius-md)',
                          boxShadow: 'var(--shadow-float)',
                          overflowX: 'hidden',
                          zIndex: 1400,
                        }}
                      >
                        {/* Email row — disabled / informational */}
                        <div
                          style={{
                            padding: 'var(--space-3) var(--space-4)',
                            fontSize: 'var(--text-caption)',
                            color: 'var(--text-muted)',
                            borderBottom: 'var(--border-width) solid var(--border)',
                            wordBreak: 'break-all',
                            cursor: 'default',
                          }}
                        >
                          {user.email}
                        </div>

                        {/* Logout — hover przez CSS (:hover/:active), nie onMouse* (W7:
                            na dotyku iPada zbędne handlery mogły blokować tap). */}
                        <style>{`.app-logout-item:hover, .app-logout-item:active { background: var(--surface-hover); }`}</style>
                        <button
                          type="button"
                          role="menuitem"
                          className="app-logout-item"
                          onClick={handleLogout}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-3)',
                            width: '100%',
                            minHeight: 56,
                            padding: '0 var(--space-4)',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontFamily: 'var(--font-sans)',
                            fontSize: 'var(--text-label)',
                            fontWeight: 'var(--weight-medium)',
                            color: 'var(--text-primary)',
                            touchAction: 'manipulation',
                          }}
                        >
                          <Icon name="arrow-right" size={22} color="var(--text-secondary)" />
                          Wyloguj
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </header>

            {/* Content */}
            <main
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: 'var(--space-7)',
                background: 'var(--surface-page)',
              }}
            >
              {renderTab()}
            </main>
          </div>
        </div>
      </ProtectedRoute>
    </ThemeProvider>
  );
}

export default App;
